/**
 * Module 00 — Global Shell · Onboarding status (PRD-onboarding §7 GATE-01..03)
 *
 * Single source of truth for "has this user finished onboarding?". Drives the
 * route guard in ProtectedRoute (un-onboarded users are redirected to
 * `/onboarding`, where the org/project/sidebar chrome never mounts) and the
 * onboarding page itself (a completed user who lands there bounces back to `/`).
 *
 * Completion is SERVER-DERIVED from either of two signals: the user owns at
 * least one project, OR they joined an org they did not found (org.isFounder
 * === false) — an invitee is onboarded the moment they land in the workspace.
 * That truth survives refreshes, new sessions and new devices without a
 * client flag. We additionally persist a localStorage marker
 * (`wx-onboarding-complete:<userId>`) so a returning user's app renders
 * instantly instead of flashing the onboarding screen while the project list
 * loads — the server truth always wins once the list resolves.
 *
 * The three states:
 *   - 'loading'  — we don't yet know (auth/scope/project list still resolving).
 *                  Callers should show a neutral spinner and NOT redirect.
 *   - 'pending'  — definitively zero projects → user must onboard.
 *   - 'complete' — user has a project (or a trusted persisted marker) → enter app.
 */

'use client';

import { useEffect } from 'react';
import { useProject } from '@/contexts/project-context';
import { useScopeStore } from '@/lib/stores/scope-store';
import { userIdFromToken } from '@/lib/shell/onboarding-telemetry';

export const ONBOARDING_ROUTE = '/onboarding';

export type OnboardingStatus = 'loading' | 'pending' | 'complete';

const MARKER_PREFIX = 'wx-onboarding-complete';
const markerKey = (userId?: string | null) =>
  userId ? `${MARKER_PREFIX}:${userId}` : MARKER_PREFIX;

function lsGet(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function lsSet(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* non-fatal */
  }
}
function lsRemove(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* non-fatal */
  }
}

/** The marker MUST be per-user: an unsuffixed key written for user A would make
 *  a different account on the same browser skip onboarding. When the caller has
 *  no userId yet (scope store not hydrated), fall back to the JWT — and if the
 *  identity still can't be resolved, do nothing rather than write/read a shared
 *  unscoped marker. */
function resolveUserId(userId?: string | null): string | undefined {
  return userId || userIdFromToken() || undefined;
}

/** Persist that the user has completed onboarding (returning-user fast path). */
export function markOnboardingComplete(userId?: string | null): void {
  const id = resolveUserId(userId);
  if (!id) return;
  lsSet(markerKey(id), '1');
}

/** Clear the marker — used when we confirm the user now has zero projects
 *  (e.g. they deleted their last project) so they are re-onboarded. */
export function clearOnboardingComplete(userId?: string | null): void {
  const id = resolveUserId(userId);
  if (!id) return;
  lsRemove(markerKey(id));
}

function hasCompletionMarker(userId?: string | null): boolean {
  const id = resolveUserId(userId);
  if (!id) return false;
  return lsGet(markerKey(id)) === '1';
}

export function useOnboardingStatus(): OnboardingStatus {
  const { projects, loadingProjects, loadedOnce } = useProject();
  const scopeProjectId = useScopeStore((s) => s.project?.id);
  const hasAvailableProjects = useScopeStore((s) => s.availableProjects.length > 0);
  const scopeUserId = useScopeStore((s) => s.user?.id);
  // Founded the org vs joined one (session bootstrap → org.isFounder). First-run
  // onboarding creates the first department + project, which only makes sense
  // for the founder — an invitee joins a workspace that already has both.
  const isFounder = useScopeStore((s) => s.org?.isFounder);
  const joinedExistingOrg = isFounder === false;
  // The scope-store user only exists once the session bootstrap hydrates; the
  // JWT identity is available from the first render. Resolving early keeps the
  // per-user marker readable during that window instead of silently missing.
  const userId = resolveUserId(scopeUserId);

  // Server truth: a project in ProjectContext, an active project in scope, OR a
  // non-empty availableProjects list from the session bootstrap. The last one
  // matters when the /projects list is empty for scoping reasons (the request
  // carries x-department-id, and a department's allow-list can legitimately
  // exclude every project) while the bootstrap proves the user owns projects.
  const hasProject = projects.length > 0 || !!scopeProjectId || hasAvailableProjects;

  // Has this user finished onboarding before (persisted marker)?
  const completedBefore = hasCompletionMarker(userId);

  // Persist completion the first time we observe a project — and NEVER auto-clear
  // it. The project signal is not stable within a session: the dept-scoped
  // project list and the scope-store project can transiently empty (scope
  // re-hydration, a dept-keyed refetch, header races). Clearing the marker on a
  // transient empty would flip status complete→pending and bounce the user to
  // /onboarding, then back once the project reappears — an infinite
  // '/' ⇄ '/onboarding' redirect loop. Stickiness is what prevents that.
  // (Marker is per-userId, so a different account never inherits it; a genuinely
  // new user simply has no marker.)
  //
  // The marker is written ONLY from the SERVER-confirmed project list
  // (`loadedOnce && projects.length > 0`), never from the scope-store project id.
  // That id can be a stale selection left by a previous account (the scope store
  // is in-memory + localStorage-backed); trusting it here would stickily mark a
  // brand-new signup "complete" and skip onboarding. The project list is fetched
  // with THIS user's token, so it can't leak another account's project.
  useEffect(() => {
    if (loadedOnce && projects.length > 0 && userId) markOnboardingComplete(userId);
  }, [loadedOnce, projects.length, userId]);

  // Sticky completion: a live project, a prior completion marker, OR having
  // joined an existing org all mean "done". Returning 'complete' even during a
  // transient empty keeps the gate stable and loop-free. joinedExistingOrg is
  // what stops an invitee with no *visible* project yet (empty department,
  // plain member) from being bounced into "create your first project".
  if (hasProject || completedBefore || joinedExistingOrg) return 'complete';

  // No project and no marker. Hold a spinner until the list has resolved at
  // least once, then route to onboarding only if it is genuinely empty.
  if (!loadedOnce || loadingProjects) return 'loading';

  // 'pending' is a per-user verdict ("THIS user never onboarded"), but the
  // marker is keyed by userId — with no resolvable identity the check above is
  // meaningless, not negative. Deciding 'pending' here intermittently bounced
  // fully-onboarded users back into onboarding whenever the scope store hadn't
  // hydrated yet. Hold 'loading' until we know who the user is.
  if (!userId) return 'loading';

  // Definitively zero projects and never onboarded → first-run onboarding.
  return 'pending';
}
